import { useRef } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { safeUrl } from "../lib/safeUrl.js";
import { useMotionMode } from "../motion/MotionDirector.jsx";
import { MOTION_TIMING, editorialEase } from "../motion/choreography.js";

export default function HeroStage({ data, profile }) {
  const contactHref = safeUrl(data.contactHref);
  const heroRef = useRef(null);
  const { mode } = useMotionMode();
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  const firstX = useTransform(scrollYProgress, [0, 1], ["0%", "-16%"]);
  const secondX = useTransform(scrollYProgress, [0, 1], ["0%", "13%"]);
  const thirdX = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  const wordY = useTransform(scrollYProgress, [0, 1], ["0%", "-58%"]);
  const sunX = useTransform(scrollYProgress, [0, 1], ["0vw", "-52vw"]);
  const sunY = useTransform(scrollYProgress, [0, 1], ["0vh", "68vh"]);
  const sunScale = useTransform(scrollYProgress, [0, 0.7, 1], [1, 0.72, 0.42]);
  const wordX = [firstX, secondX, thirdX];

  return (
    <section ref={heroRef} id="hero" className="hero" aria-labelledby="hero-title">
      <motion.div
        className="hero__sun"
        aria-hidden="true"
        style={mode === "full" ? { x: sunX, y: sunY, scale: sunScale } : undefined}
      />
      <div className="hero__topline">
        <span>{data.eyebrow}</span>
        <span>Portfolio / 2026</span>
      </div>
      <motion.h1
        id="hero-title"
        className="hero__title"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: {},
          visible: {
            transition: { staggerChildren: MOTION_TIMING.heroStagger, delayChildren: 0.12 }
          }
        }}
      >
        {data.headline.map((word, index) => (
          <motion.span
            className={`hero__word hero__word--${index + 1}`}
            key={word}
            style={mode === "full" ? { x: wordX[index], y: wordY } : undefined}
            variants={{
              hidden: { y: "115%", rotate: index === 1 ? 4 : -3 },
              visible: {
                y: "0%",
                rotate: 0,
                transition: {
                  duration: MOTION_TIMING.heroDuration,
                  ease: editorialEase
                }
              }
            }}
          >
            {word}
          </motion.span>
        ))}
      </motion.h1>
      <motion.div
        className="hero__footer"
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.62, duration: 0.62 }}
      >
        <p>{data.statement}</p>
        <div className="hero__identity">
          <strong>{profile.nameZh}</strong>
          <span>{profile.role}</span>
        </div>
        {contactHref && (
          <a className="round-link" href={contactHref}>
            <span>{data.contactLabel}</span>
            <b aria-hidden="true">↗</b>
          </a>
        )}
      </motion.div>
      <motion.div
        className="hero__rail"
        aria-hidden="true"
        animate={mode === "static" ? undefined : { x: ["0%", "-8%", "0%"] }}
        transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
      >
        <span>CREATIVE DIRECTION</span>
        <i />
        <span>REALTIME GRAPHICS</span>
        <i />
        <span>SPATIAL EXPERIENCE</span>
      </motion.div>
    </section>
  );
}
